import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import { FormTextInput } from "UIComponents/InputTextField";
import { FormSelectDropdown } from "UIComponents/SelectDropdown";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import BulkUploadErrorModal from "UIComponents/BulkUploadErrorModal";
import classes from "./ManageStandard.scss";
import { CloseOutlined, AddOutlined } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";
import { getCountryDropdownDataMemoize } from "Administrator/modules/Utils";
import { AttachmentItem } from "Attachment";
import { CURRICULUM_FLAVOURS } from "Tooling/modules/ToolingUtils";
import { updateStandardInfo } from "Tooling/modules/ToolingModule";
import { bulkUploadByFile } from "Tooling/modules/ToolingThunks";

const styles = {
  textInputContainerStyle: {
    flex: 1,
  },
  customModalStyle: {
    content: {
      borderRadius: 12,
    },
  },
};

const ManageStandard = ({
  closeModal,
  headerTitle,
  mode,
  subjectDropdownOptions,
  updateStandardInfo,
  getStandardFeed,
  bulkUploadByFile,
  standardObject,
}) => {
  const titleRef = useRef();
  const curriculumNameRef = useRef();
  const codeRef = useRef();
  const curriculumTypesRef = useRef();

  const initialStandardItemDetails = {
    id: "",
    title: "",
    code: "",
    curriculumName: "",
    curriculumTypes: [],
    country: "",
    state: "",
    subjects: [],
    type: "STANDARD_BENCHMARK_SET_V2",
    url: {},
    description: "",
  };

  const [standardItemDetails, setStandardItemDetails] = useState(
    initialStandardItemDetails
  );
  const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
  const [showBulkUploadErrorModal, setShowBulkUploadErrorModal] = useState(
    false
  );
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (mode === "edit") {
      const { globalSubjects } = standardObject;
      const standardObj = _.pick(standardObject, [
        "id",
        "code",
        "country",
        "state",
        "curriculumName",
        "curriculumTypes",
        "title",
      ]);
      setStandardItemDetails(prevState => ({
        ...prevState,
        ...standardObj,
        subjects: globalSubjects,
      }));
    }
  }, []);

  const updateStandardItem = params => {
    setStandardItemDetails(prevState => ({ ...prevState, ...params }));
  };

  const onContentUploaded = data => {
    updateStandardItem({ url: data[0] });
  };

  const deleteAttachment = () => {
    setStandardItemDetails(prevState => ({
      ...prevState,
      url: {},
    }));
  };

  const toggleBulkUploadErrorModal = () => {
    setShowBulkUploadErrorModal(!showBulkUploadErrorModal);
  };

  const onSave = async () => {
    setIsFullScreenLoading(true);
    const {
      subjects,
      url: { url },
      type,
    } = standardItemDetails;
    switch (mode) {
      case "edit": {
        const isSuccess = await updateStandardInfo({
          input: _.omit(standardItemDetails, ["url", "type"]),
        });
        if (isSuccess) {
          closeModal();
          getStandardFeed.refetch();
        }
        break;
      }
      case "create": {
        const { successful, errorsJSON } = await bulkUploadByFile({
          url,
          type,
          inputs: {
            subjects,
            standardSetDetails: _.omit(standardItemDetails, [
              "url",
              "type",
              "subjects",
              "id",
            ]),
          },
        });
        if (successful) {
          closeModal();
          getStandardFeed.refetch();
        } else {
          setErrors(errorsJSON);
          toggleBulkUploadErrorModal();
        }
        break;
      }
    }
    setIsFullScreenLoading(false);
  };

  const onClickRightButton = () => {
    const refsList = [titleRef, curriculumNameRef, codeRef, curriculumTypesRef];
    const totalErrors = _.reduce(
      refsList,
      (result, ref) => result + ref.current.isValid(),
      0
    );
    if (totalErrors == 0) {
      onSave();
    }
  };

  const renderBenchmarkButtonComponent = () => {
    return (
      <Button
        size={"medium"}
        variant={"outlined-subtle"}
        icon={<AddOutlined />}
      >
        {"Import benchmark set"}
      </Button>
    );
  };

  const renderBenchmarkSet = () => {
    const { type, url } = standardItemDetails;
    const attachment = { title: url.name, type: type };

    return (
      <div className={classes.horizontalRowInput}>
        <div className={classes.horizontalUploadElement}>
          <div className={classes.fileComponent}>
            {_.isEmpty(url) ? (
              <DropzoneComponent
                dropZoneStyle={styles.dropZoneStyle}
                key={type}
                multiple={false}
                renderComponent={renderBenchmarkButtonComponent()}
                accept={[".csv", "text/csv"]}
                onContentUploaded={onContentUploaded}
              />
            ) : (
              <AttachmentItem
                attachment={attachment}
                mode={"edit"}
                deleteAttachment={deleteAttachment}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const {
    title,
    curriculumName,
    code,
    country,
    state,
    curriculumTypes,
    subjects,
  } = standardItemDetails;

  const countryData = getCountryDropdownDataMemoize();

  const isCreateMode = mode === "create";

  return (
    <UIModal
      isOpen={true}
      onRequestClose={closeModal}
      customStyle={styles.customModalStyle}
    >
      <div className={classes.container}>
        <div className={classes.modalHeader}>
          <div className={classes.titleText}>{headerTitle}</div>
          <div className={classes.closeIcon} onClick={closeModal}>
            <CloseOutlined />
          </div>
        </div>

        <div className={classes.innerContainer}>
          <div className={classes.horizontalRowInput}>
            <FormTextInput
              size={"large"}
              label={"Standard name"}
              value={title}
              placeholder={"Enter standard name"}
              name={"title"}
              updateInputField={updateStandardItem}
              ref={titleRef}
              error={"Enter standard name"}
              isRequired
            />
          </div>

          <div className={classes.horizontalRowInput}>
            <FormTextInput
              size={"large"}
              label={"Curriculum name"}
              value={curriculumName}
              placeholder={"Enter curriculum name"}
              name={"curriculumName"}
              updateInputField={updateStandardItem}
              ref={curriculumNameRef}
              error={"Enter curriculum name"}
              containerStyle={styles.textInputContainerStyle}
              isRequired
            />

            <FormTextInput
              size={"large"}
              label={"Code"}
              value={code}
              placeholder={"Enter code"}
              name={"code"}
              updateInputField={updateStandardItem}
              ref={codeRef}
              error={"Enter code"}
              containerStyle={styles.textInputContainerStyle}
              isRequired
            />
          </div>

          <div className={classes.horizontalRowInput}>
            <FormSelectDropdown
              name={"country"}
              options={countryData}
              label={"Country"}
              updateInputField={updateStandardItem}
              placeholder={"Select"}
              value={country}
              size={"medium"}
              containerStyle={styles.textInputContainerStyle}
            />

            <FormTextInput
              size={"large"}
              label={"State/ province"}
              value={state}
              placeholder={"Enter State"}
              name={"state"}
              updateInputField={updateStandardItem}
              containerStyle={styles.textInputContainerStyle}
            />
          </div>

          <div className={classes.horizontalRowInput}>
            <FormSelectDropdown
              name={"curriculumTypes"}
              options={CURRICULUM_FLAVOURS}
              label={"Curriculum flavor"}
              updateInputField={updateStandardItem}
              placeholder={"Select"}
              value={curriculumTypes}
              size={"large"}
              multi={true}
              containerStyle={styles.textInputContainerStyle}
              ref={curriculumTypesRef}
              error={"Select curriculum"}
              isRequired
            />
          </div>

          <div className={classes.horizontalRowInput}>
            <FormSelectDropdown
              name={"subjects"}
              options={subjectDropdownOptions}
              label={"Subject(s)"}
              updateInputField={updateStandardItem}
              placeholder={"Select"}
              value={subjects}
              size={"large"}
              multi={true}
              containerStyle={styles.textInputContainerStyle}
            />
          </div>
          {isCreateMode ? renderBenchmarkSet() : null}
        </div>

        <div className={classes.footer}>
          <Button
            variant={"outlined-subtle"}
            size={"medium"}
            onClick={closeModal}
          >
            {"Cancel"}
          </Button>
          <Button
            variant={"progressive"}
            size={"medium"}
            onClick={onClickRightButton}
            disabled={isCreateMode ? _.isEmpty(standardItemDetails.url) : false}
          >
            {isCreateMode ? "Create standard" : "Save"}
          </Button>
        </div>
        {showBulkUploadErrorModal && (
          <BulkUploadErrorModal
            toggleBulkUploadErrorModal={toggleBulkUploadErrorModal}
            errors={errors}
          />
        )}
      </div>
      {!!isFullScreenLoading && <FullScreenLoader />}
    </UIModal>
  );
};

const mapActionCreators = {
  updateStandardInfo,
  bulkUploadByFile,
};

export default compose(connect(null, mapActionCreators))(ManageStandard);
